/**
 * <s-judgeme-reviews> — Numbered pages for the product reviews list (1, 2, 3 …).
 *
 * Page 1 is rendered by Liquid from the product's Judge.me review data. Choosing
 * another page fetches it from Judge.me's public widget endpoint (same order and
 * page size) and replaces the cards with ones built from <template data-review-template>.
 *
 * Attributes (on <s-judgeme-reviews>):
 *   data-endpoint, data-shop-domain, data-product-id — Judge.me request parameters.
 *   data-per-page        — Reviews per page; must match the server-rendered count.
 *   data-total-pages     — Total pages.
 *   data-verified-label, data-reply-label — Translated card labels.
 *   data-rating-label    — Translated label with __RATING__ placeholder.
 *   data-photo-alt       — Translated alt text with __NAME__ placeholder.
 *   data-error-message   — Translated message shown when loading fails.
 *
 * Required child markup:
 *   [data-review-list]     — The <ul> of cards.
 *   [data-review-template] — <template> holding one card.
 *   [data-pagination]      — <nav> the page buttons are written into; carries
 *                            data-prev-label, data-next-label and data-page-label (__N__).
 *   [data-load-status]     — Status line for errors.
 */
class JudgemeReviews extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-review-list]');
    this.template = this.querySelector('[data-review-template]');
    this.nav = this.querySelector('[data-pagination]');
    this.status = this.querySelector('[data-load-status]');
    this.currentPage = 1;
    this.totalPages = Number(this.dataset.totalPages || 1);
    this.cache = new Map();
    this.handleClick = this.onNavClick.bind(this);
    this.nav?.addEventListener('click', this.handleClick);
    this.renderNav();
  }

  disconnectedCallback() {
    this.nav?.removeEventListener('click', this.handleClick);
  }

  onNavClick(event) {
    const button = event.target.closest('button[data-page]');
    if (!button || button.disabled) return;
    this.goToPage(Number(button.dataset.page));
  }

  async fetchPage(page) {
    if (this.cache.has(page)) return this.cache.get(page);
    const url = new URL(this.dataset.endpoint);
    url.search = new URLSearchParams({
      url: this.dataset.shopDomain,
      shop_domain: this.dataset.shopDomain,
      platform: 'shopify',
      product_id: this.dataset.productId,
      page: String(page),
      per_page: this.dataset.perPage,
    }).toString();
    const response = await fetch(url);
    if (!response.ok) throw new Error(String(response.status));
    const data = await response.json();
    if (data.pagination && data.pagination.total_pages) this.totalPages = Number(data.pagination.total_pages);
    const reviews = Array.isArray(data.reviews) ? data.reviews : [];
    this.cache.set(page, reviews);
    return reviews;
  }

  async goToPage(page) {
    if (!page || page === this.currentPage || page < 1 || page > this.totalPages) return;
    this.status.textContent = '';
    this.setAttribute('aria-busy', 'true');
    this.nav.querySelectorAll('button').forEach((button) => (button.disabled = true));
    try {
      if (!this.firstPageCards) this.firstPageCards = Array.from(this.list.children).map((card) => card.cloneNode(true));
      const cards = page === 1 && this.firstPageCards
        ? this.firstPageCards.map((card) => card.cloneNode(true))
        : (await this.fetchPage(page)).map((review) => this.buildCard(review));
      this.list.replaceChildren(...cards);
      this.currentPage = page;
      this.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      const first = this.list.firstElementChild;
      if (first) {
        first.setAttribute('tabindex', '-1');
        first.focus({ preventScroll: true });
      }
    } catch (_) {
      this.status.textContent = this.dataset.errorMessage;
    } finally {
      this.removeAttribute('aria-busy');
      this.renderNav();
    }
  }

  /** Page numbers to show: first, last, and two either side of the current page. */
  pageItems() {
    const total = this.totalPages;
    const current = this.currentPage;
    const pages = new Set([1, total]);
    for (let page = current - 2; page <= current + 2; page += 1) {
      if (page >= 1 && page <= total) pages.add(page);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const items = [];
    sorted.forEach((page, index) => {
      if (index > 0 && page - sorted[index - 1] > 1) items.push('gap');
      items.push(page);
    });
    return items;
  }

  makeButton(label, page, { current = false, disabled = false, text } = {}) {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.page = String(page);
    button.setAttribute('aria-label', label);
    button.textContent = text ?? String(page);
    button.disabled = disabled;
    button.className = current
      ? 'inline-flex size-11 items-center justify-center rounded-full border-2 border-accent bg-accent text-sm font-semibold text-interactive-text'
      : 'inline-flex size-11 items-center justify-center rounded-full border-2 border-accent/30 bg-surface text-sm font-semibold text-on-surface hover:border-accent disabled:opacity-40';
    if (current) button.setAttribute('aria-current', 'page');
    return button;
  }

  renderNav() {
    if (!this.nav) return;
    const pageLabel = (n) => this.nav.dataset.pageLabel.replace('__N__', String(n));
    const nodes = [];
    nodes.push(this.makeButton(this.nav.dataset.prevLabel, this.currentPage - 1, { disabled: this.currentPage <= 1, text: '‹' }));
    this.pageItems().forEach((item) => {
      if (item === 'gap') {
        const gap = document.createElement('span');
        gap.className = 'px-1 text-on-surface-secondary';
        gap.setAttribute('aria-hidden', 'true');
        gap.textContent = '…';
        nodes.push(gap);
      } else {
        nodes.push(this.makeButton(pageLabel(item), item, { current: item === this.currentPage }));
      }
    });
    nodes.push(this.makeButton(this.nav.dataset.nextLabel, this.currentPage + 1, { disabled: this.currentPage >= this.totalPages, text: '›' }));
    this.nav.replaceChildren(...nodes);
  }

  buildCard(review) {
    const card = this.template.content.firstElementChild.cloneNode(true);
    const rating = Math.round(Number(review.rating) || 0);
    const stars = card.querySelector('[data-stars]');
    stars.setAttribute('aria-label', this.dataset.ratingLabel.replace('__RATING__', String(rating)));
    stars.querySelectorAll('path').forEach((path, index) => {
      path.setAttribute('fill', index < rating ? 'currentColor' : 'none');
    });

    const created = review.created_at ? new Date(review.created_at) : null;
    const time = card.querySelector('[data-date]');
    if (created && !Number.isNaN(created.getTime())) {
      time.dateTime = created.toISOString().slice(0, 10);
      time.textContent = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { dateStyle: 'medium' }).format(created);
    }

    const title = this.plain(review.title);
    if (title) {
      const titleEl = card.querySelector('[data-title]');
      titleEl.textContent = title;
      titleEl.hidden = false;
    }
    card.querySelector('[data-body]').textContent = this.plain(review.body || review.body_html);

    const name = this.plain(review.reviewer_name);
    const pictures = Array.isArray(review.pictures_urls) ? review.pictures_urls.slice(0, 4) : [];
    if (pictures.length) {
      const photos = card.querySelector('[data-photos]');
      pictures.forEach((picture) => {
        if (!picture || !picture.small) return;
        const link = document.createElement('a');
        link.href = picture.original || picture.small;
        link.target = '_blank';
        link.rel = 'noopener';
        link.className = 'block size-16 overflow-hidden rounded-lg ring-1 ring-border';
        const img = document.createElement('img');
        img.src = picture.small;
        img.alt = this.dataset.photoAlt.replace('__NAME__', name);
        img.width = 64;
        img.height = 64;
        img.loading = 'lazy';
        img.className = 'size-full object-cover';
        link.append(img);
        photos.append(link);
      });
      photos.hidden = photos.children.length === 0;
    }

    card.querySelector('[data-initial]').textContent = this.plain(review.reviewer_initial) || name.charAt(0) || '?';
    card.querySelector('[data-name]').textContent = name;
    if (review.verified_buyer) {
      const verified = card.querySelector('[data-verified]');
      verified.textContent = this.dataset.verifiedLabel;
      verified.hidden = false;
    }

    const reply = this.plain(review.reply_content);
    if (reply) {
      card.querySelector('[data-reply-label]').textContent = this.dataset.replyLabel;
      card.querySelector('[data-reply-body]').textContent = reply;
      card.querySelector('[data-reply]').hidden = false;
    }
    return card;
  }

  /** Converts Judge.me text or HTML into plain text without rendering it. */
  plain(value) {
    if (!value) return '';
    const doc = new DOMParser().parseFromString(String(value), 'text/html');
    return (doc.body.textContent || '').trim();
  }
}

if (!customElements.get('s-judgeme-reviews')) customElements.define('s-judgeme-reviews', JudgemeReviews);
